import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/platform', withCredentials: true });

const inputStyle = {
    width: '100%', padding: '0.75rem', borderRadius: '0.5rem',
    border: '1px solid var(--input-border)', background: 'var(--input-bg)',
    color: 'var(--text-main)', fontSize: '0.9rem',
};
const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.375rem' };

function Field({ label, hint, children }) {
    return (
        <div>
            <label style={labelStyle}>{label}</label>
            {children}
            {hint && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>{hint}</p>}
        </div>
    );
}

// The single, platform-wide SMTP setup — tenants/orgs have no SMTP config of
// their own; every email the app sends (welcome emails, password resets,
// leave/expense notifications) goes out through this one mailbox.
export default function PlatformSettingsModal({ onClose }) {
    const [form, setForm] = useState({ smtpHost: '', smtpPort: '587', smtpUser: '', smtpFrom: '', smtpPassword: '' });
    const [passwordMasked, setPasswordMasked] = useState('');
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [testTo, setTestTo] = useState('');
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        api.get('settings').then(res => {
            setForm(f => ({ ...f, ...res.data, smtpPassword: '' }));
            setPasswordMasked(res.data.smtpPasswordMasked || '');
        }).catch(() => setResult({ ok: false, message: 'Failed to load settings' }))
            .finally(() => setLoading(false));
    }, []);

    const mutation = useMutation({
        mutationFn: (data) => api.patch('settings', data),
        onSuccess: () => setResult({ ok: true, message: 'Platform SMTP settings saved.' }),
        onError: (err) => setResult({ ok: false, message: err.response?.data?.error || 'Failed to save' }),
    });

    const testMutation = useMutation({
        mutationFn: () => api.post('settings/test-email', { ...form, to: testTo }),
        onSuccess: (res) => setTestResult({ ok: true, message: res.data.message }),
        onError: (err) => setTestResult({ ok: false, message: err.response?.data?.error || 'Failed to send test email' }),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        mutation.mutate(form);
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
            <div style={{ background: 'var(--surface)', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.2)', width: '100%', maxWidth: '32rem', maxHeight: '90vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                    <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-main)' }}>Platform Settings — Email (SMTP)</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.5rem', lineHeight: 1 }}>&times;</button>
                </div>

                {loading ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5, background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                            This is the single mailbox every organisation's emails are sent through — welcome emails,
                            password resets, and leave/expense notifications for ALL tenants. Orgs cannot configure
                            their own SMTP. For Gmail with 2FA, use <strong>smtp.gmail.com</strong> (port 587) and a
                            16-character <strong>App Password</strong>.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            <Field label="SMTP Host">
                                <input value={form.smtpHost} onChange={e => setForm(f => ({ ...f, smtpHost: e.target.value }))}
                                    style={inputStyle} placeholder="smtp.gmail.com" />
                            </Field>
                            <Field label="SMTP Port">
                                <input value={form.smtpPort} onChange={e => setForm(f => ({ ...f, smtpPort: e.target.value }))}
                                    style={inputStyle} placeholder="587" />
                            </Field>
                        </div>

                        <Field label="SMTP User (mailbox)" hint="Usually a mailbox address, but some providers (e.g. Resend) use a fixed username like &quot;resend&quot; instead.">
                            <input type="text" value={form.smtpUser} onChange={e => setForm(f => ({ ...f, smtpUser: e.target.value }))}
                                style={inputStyle} placeholder="notifications@yourdomain.com" />
                        </Field>

                        <Field label="SMTP Password / App Password" hint={passwordMasked ? `Currently set (${passwordMasked}). Leave blank to keep it.` : 'Not set yet.'}>
                            <input type="password" value={form.smtpPassword} onChange={e => setForm(f => ({ ...f, smtpPassword: e.target.value }))}
                                style={inputStyle} placeholder="••••••••••••••••" />
                        </Field>

                        <Field label="Sender Email (From)" hint="The address shown to recipients.">
                            <input type="email" value={form.smtpFrom} onChange={e => setForm(f => ({ ...f, smtpFrom: e.target.value }))}
                                style={inputStyle} placeholder="notifications@yourdomain.com" />
                        </Field>

                        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <Field label="Send a test email" hint="Tries the fields above as typed, even if unsaved — a blank password falls back to whatever's already saved. Leave the address blank to send to yourself.">
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                                        style={{ ...inputStyle, flex: 1 }} placeholder={form.smtpUser?.includes('@') ? form.smtpUser : 'you@example.com'} />
                                    <button type="button" onClick={() => { setTestResult(null); testMutation.mutate(); }}
                                        disabled={testMutation.isPending} className="btn btn-secondary"
                                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}>
                                        {testMutation.isPending ? 'Sending…' : 'Send Test Email'}
                                    </button>
                                </div>
                            </Field>
                            {testResult && (
                                <div style={{
                                    fontSize: '0.875rem', borderRadius: '0.5rem', padding: '0.5rem 0.75rem',
                                    background: testResult.ok ? '#dcfce7' : '#fee2e2', color: testResult.ok ? '#166534' : '#991b1b',
                                }}>
                                    {testResult.message}
                                </div>
                            )}
                        </div>

                        {result && (
                            <div style={{
                                fontSize: '0.875rem', borderRadius: '0.5rem', padding: '0.5rem 0.75rem',
                                background: result.ok ? '#dcfce7' : '#fee2e2', color: result.ok ? '#166534' : '#991b1b',
                            }}>
                                {result.message}
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
                            <button type="button" onClick={onClose} className="btn btn-secondary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                                Close
                            </button>
                            <button type="submit" disabled={mutation.isPending} className="btn btn-primary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                                {mutation.isPending ? 'Saving…' : 'Save Settings'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
